import React, { useMemo, useState } from "react";
import Icon from "../Icon";
import MockFormModal from "./MockFormModal";
import { matchesMockSearch } from "./mockData";

const EMPTY_EMAIL_FORM = { name: "", address: "", department: "Customer Support", deptColor: "#92400e" };

const MockEmails = ({ emails, departments, onAddEmail, onUpdateEmail, onDeleteEmail, showConfirm, searchQuery = "" }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_EMAIL_FORM);
    const [error, setError] = useState("");

    const filteredEmails = useMemo(
        () => emails.filter((e) =>
            matchesMockSearch(searchQuery, e.name, e.address, e.department, e.received)
        ),
        [emails, searchQuery]
    );

    const resetForm = () => {
        setForm(EMPTY_EMAIL_FORM);
        setError("");
        setEditingId(null);
        setShowModal(false);
    };

    const openAddModal = () => {
        setEditingId(null);
        setForm(EMPTY_EMAIL_FORM);
        setError("");
        setShowModal(true);
    };

    const openEditModal = (email) => {
        setEditingId(email.id);
        setForm({
            name: email.name,
            address: email.address,
            department: email.department,
            deptColor: email.deptColor,
        });
        setError("");
        setShowModal(true);
    };

    const handleDepartmentChange = (deptName) => {
        const dept = departments.find((d) => d.name === deptName);
        setForm((f) => ({
            ...f,
            department: deptName,
            deptColor: dept?.color || f.deptColor,
        }));
    };

    const handleSaveEmail = () => {
        const name = form.name.trim();
        const address = form.address.trim().toLowerCase();
        if (!name) {
            setError("Enter a display name.");
            return;
        }
        if (!address || !address.includes("@")) {
            setError("Enter a valid email address.");
            return;
        }
        const duplicate = emails.some(
            (e) => e.address.toLowerCase() === address && e.id !== editingId
        );
        if (duplicate) {
            setError("This email address is already configured.");
            return;
        }

        const payload = {
            name,
            address,
            department: form.department,
            deptColor: form.deptColor,
        };

        if (editingId) {
            onUpdateEmail(editingId, payload);
        } else {
            onAddEmail(payload);
        }
        resetForm();
    };

    const handleDeleteEmail = (email) => {
        showConfirm?.({
            title: "Are you sure you want to remove this email?",
            message: "You will not be able to recover it.",
            onConfirm: () => {
                onDeleteEmail(email.id);
                if (editingId === email.id) resetForm();
            },
        });
    };

    return (
        <div className="clk-mk-page clk-mk-page--emails">
            <div className="clk-mk-page-header-row">
                <h5>Incoming Emails</h5>
                <div className="clk-mk-header-actions">
                    <button type="button" className="clk-mk-outline-btn" disabled title="Not available in demo">
                        <Icon name="image" size={14} />
                        Add Company Logo
                    </button>
                    <button type="button" className="clk-mk-primary-btn" onClick={openAddModal}>
                        <img src="/images/plus.svg" alt="" aria-hidden="true" />
                        Add Email
                    </button>
                </div>
            </div>

            {showModal && (
                <MockFormModal
                    title={editingId ? "Edit email" : "New incoming email"}
                    onClose={resetForm}
                >
                    <label className="clk-mk-field">
                        <span>Name</span>
                        <input
                            type="text"
                            value={form.name}
                            placeholder="e.g. Support"
                            onChange={(e) => {
                                const { value } = e.target;
                                setForm((f) => ({ ...f, name: value }));
                                setError("");
                            }}
                        />
                    </label>
                    <label className="clk-mk-field">
                        <span>Email address</span>
                        <input
                            type="email"
                            value={form.address}
                            placeholder="yourcompany@claritick.com"
                            onChange={(e) => {
                                const { value } = e.target;
                                setForm((f) => ({ ...f, address: value }));
                                setError("");
                            }}
                        />
                    </label>
                    <label className="clk-mk-field">
                        <span>Department</span>
                        <select
                            value={form.department}
                            onChange={(e) => handleDepartmentChange(e.target.value)}
                        >
                            {departments.map((d) => (
                                <option key={d.id} value={d.name}>{d.name}</option>
                            ))}
                        </select>
                    </label>
                    {error && <p className="clk-mk-form-error">{error}</p>}
                    <div className="clk-mk-create-actions">
                        <button type="button" className="clk-mk-link-btn" onClick={resetForm}>
                            Cancel
                        </button>
                        <button type="button" className="clk-mk-primary-btn" onClick={handleSaveEmail}>
                            {editingId ? "Save changes" : "Add email"}
                        </button>
                    </div>
                </MockFormModal>
            )}

            <div className="clk-mk-emails-layout">
                <div className="clk-mk-emails-table-wrap">
                    {filteredEmails.length === 0 ? (
                        <p className="clk-mk-empty">No emails match your search.</p>
                    ) : (
                        <table className="clk-mk-app-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email Address</th>
                                    <th>Received Emails</th>
                                    <th>Department</th>
                                    <th className="clk-mk-col-actions" aria-label="Actions">
                                        <span className="clk-mk-sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmails.map((e) => (
                                    <tr key={e.id}>
                                        <td>{e.name}</td>
                                        <td>{e.address}</td>
                                        <td>{e.received}</td>
                                        <td>
                                            <span className="clk-mk-dept-badge">
                                                <i style={{ background: e.deptColor }} />
                                                {e.department}
                                            </span>
                                        </td>
                                        <td className="clk-mk-col-actions">
                                            <div className="clk-mk-row-actions">
                                                <button
                                                    type="button"
                                                    className="clk-mk-icon-btn"
                                                    aria-label={`Edit ${e.name}`}
                                                    onClick={() => openEditModal(e)}
                                                >
                                                    <Icon name="pencil" size={13} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="clk-mk-icon-btn clk-mk-icon-btn--danger"
                                                    aria-label={`Delete ${e.name}`}
                                                    onClick={() => handleDeleteEmail(e)}
                                                >
                                                    <Icon name="trash" size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <div className="clk-mk-table-footer clk-mk-table-footer--simple">
                        <div className="clk-mk-pagination">
                            <button type="button" className="clk-mk-page-btn is-active">1</button>
                        </div>
                        <span className="clk-mk-results-count">
                            Showing {filteredEmails.length} of {emails.length}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MockEmails;
